import React from 'react';
import { Link } from 'react-router-dom';

import { useTranslation } from '../i18n';

/**
 * A proportional breakdown: label, bar, value, and an optional trailing figure.
 *
 * A LIST, not a pie. These numbers are read for exact values («how many
 * expired?»), which is a table's job, and six near-equal slices are unreadable
 * anyway. The bar is there to make the shape of the estate scannable, not to be
 * measured — which is why the exact number sits beside it rather than in a
 * tooltip.
 *
 * ROWS CAN BE LINKS. Each of these numbers names a filtered list that already
 * exists one click away, so a breakdown that cannot be clicked makes an operator
 * re-find the filter by hand. A row with no `href` renders as a plain row, which
 * is right for the ones no filter can express (قدیمی, دائمی).
 *
 * The label column is FLUID with a floor, not a fixed width. Fixed `w-36`
 * truncated «بدون انقضا (قدیمی)» in Persian and left half the column empty in
 * English — a layout sized to one language's captions breaks in the other
 * (DESIGN-CONCEPT §16.6).
 *
 * Props:
 *   rows  [{ key, label, value, percent, tone, extra, href, title }]
 *   note  a quiet line under the list (the "none of these" summary)
 */
export default function BreakdownList({ rows = [], note, 'data-testid': testId }) {
  const { t } = useTranslation();

  return (
    <div data-testid={testId}>
      <ul className="flex flex-col gap-3">
        {rows.map((row) => {
          // A label may be a NODE (a model id inside an LTR island); the string
          // for `title` and the accessible name then comes from `row.title`.
          const text = row.title ?? (typeof row.label === 'string' ? row.label : '');
          const body = (
            <>
              {/*
                Every text column SHRINKS and the bar is the only fixed one.

                With a fixed label floor plus a fixed money column, the row's
                minimum width exceeded the rail's at Desktop — so the money ran
                out past the card's edge, in English, where the words are longer.
                A layout sized to one language's captions breaks in the other
                (§16.6), and a fixed `w-28` is exactly that in miniature.
              */}
              <span
                className="min-w-0 flex-[2] truncate text-sm text-muted-foreground"
                title={text}
              >
                {row.label}
              </span>
              <span className="h-2.5 w-[22%] min-w-[2.5rem] shrink-0 overflow-hidden rounded-full bg-muted">
                <span
                  className="block h-full rounded-full transition-[width] duration-300"
                  // A computed proportion cannot be a utility class; the colour is
                  // still a token, never a literal.
                  style={{ width: `${row.percent}%`, background: row.tone }}
                />
              </span>
              <span className="w-10 shrink-0 text-end text-sm font-semibold tabular-nums text-foreground">
                {row.value}
              </span>
              {row.extra !== undefined && (
                <span
                  className="min-w-0 flex-1 truncate text-end text-xs tabular-nums text-muted-foreground"
                  title={typeof row.extra === 'string' ? row.extra : undefined}
                >
                  {row.extra}
                </span>
              )}
            </>
          );

          return (
            <li key={row.key}>
              {row.href ? (
                <Link
                  to={row.href}
                  className="flex items-center gap-3 rounded-lg py-0.5 outline-none transition-colors hover:bg-muted focus-visible:ring-2 focus-visible:ring-ring"
                  // The bar and the percentage are decoration around a number that
                  // is already in the row, so the link's accessible name is built
                  // from the parts that carry meaning.
                  aria-label={t('dashboard.row.shareOf', {
                    label: text, value: row.value, percent: `${row.percent}%`,
                  })}
                >
                  {body}
                </Link>
              ) : (
                <div className="flex items-center gap-3 py-0.5">{body}</div>
              )}
            </li>
          );
        })}
      </ul>

      {note ? (
        <p className="mt-4 border-t border-border pt-3 text-xs text-muted-foreground">
          {note}
        </p>
      ) : null}
    </div>
  );
}
